import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useLanguage } from '../../context/LanguageContext';
import Icon from '../../components/Icon';
import styles from './Auth.module.css';


const Auth = ({ onAuthSuccess }) => {
  const { login, signup, setGuestUser, theme, toggleTheme } = useApp();
  const { t, language, toggleLanguage } = useLanguage();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // One problem at a time: { field, message }. `field` is an input's name, or
  // 'form' for the server's answer, and decides where the message appears.
  const [error, setError] = useState(null);
  
  // Login form state
  const [loginData, setLoginData] = useState({
    username: '',
    password: ''
  });
  
  // Signup form state
  const [signupData, setSignupData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Handle guest continue
  const handleGuestContinue = () => {
    setGuestUser(); // Use context function instead of callback
  };

  /**
   * ═══════════════════════════════════════════════════════════════
   * WEBHOOK: Login
   * ═══════════════════════════════════════════════════════════════
   * 
   * Endpoint: http://localhost:5678/webhook/login
   * Method: POST
   * 
   * REQUEST BODY:
   * {
   *   username: string,              // Username or email
   *   password: string               // User password
   * }
   * 
   * EXPECTED RESPONSE (Success):
   * {
   *   success: true,
   *   user: {
   *     id: string,
   *     username: string,
   *     email: string,
   *     avatar: string,
   *     isGuest: false,
   *     createdAt: string (ISO 8601)
   *   },
   *   token: string,                 // JWT token or session ID
   *   message: string
   * }
   * 
   * EXPECTED RESPONSE (Error):
   * {
   *   success: false,
   *   error: string,                 // Error message
   *   code: string                   // Error code (e.g., "AUTH_FAILED")
   * }
   * ═══════════════════════════════════════════════════════════════
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.username || !loginData.password) {
      setError({ field: loginData.username ? 'password' : 'username', message: t('auth.fillAllFields') });
      return;
    }

    setError(null);
    setLoading(true);
    
    try {
      const result = await login(loginData.username, loginData.password);
      
      // On success the context signs the user in and this page goes away.
      if (!result.success) setError({ field: 'form', message: failureText(result, 'auth.loginFailed') });
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * ═══════════════════════════════════════════════════════════════
   * WEBHOOK: Signup
   * ═══════════════════════════════════════════════════════════════
   * 
   * Endpoint: http://localhost:5678/webhook/signup
   * Method: POST
   * 
   * REQUEST BODY:
   * {
   *   username: string,              // Desired username (unique)
   *   email: string,                 // User email address
   *   password: string,              // User password
   *   confirmPassword: string        // Password confirmation
   * }
   * 
   * EXPECTED RESPONSE (Success):
   * {
   *   success: true,
   *   user: {
   *     id: string,
   *     username: string,
   *     email: string,
   *     avatar: string,
   *     isGuest: false,
   *     createdAt: string (ISO 8601)
   *   },
   *   token: string,
   *   message: string
   * }
   * 
   * EXPECTED RESPONSE (Error):
   * {
   *   success: false,
   *   error: string,                 // Error message
   *   code: string                   // Error code (e.g., "USERNAME_TAKEN")
   * }
   * ═══════════════════════════════════════════════════════════════
   */
  const handleSignup = async (e) => {
    e.preventDefault();
    
    const empty = ['username', 'email', 'password', 'confirmPassword'].find((name) => !signupData[name]);
    if (empty) {
      setError({ field: empty, message: t('auth.fillAllFields') });
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      setError({ field: 'confirmPassword', message: t('auth.passwordMismatch') });
      return;
    }

    if (signupData.password.length < 6) {
      setError({ field: 'password', message: t('auth.passwordTooShort') });
      return;
    }

    setError(null);
    setLoading(true);
    
    try {
      const result = await signup({
        username: signupData.username,
        email: signupData.email,
        password: signupData.password,
        confirmPassword: signupData.confirmPassword
      });
      
      if (!result.success) setError({ field: 'form', message: failureText(result, 'auth.signupFailed') });
    } catch (error) {
      console.error('Signup error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginInputChange = (e) => {
    setError(null);
    setLoginData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSignupInputChange = (e) => {
    setError(null);
    setSignupData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // The workflow's own message when it sent one, otherwise ours.
  const failureText = (result, fallbackKey) =>
    result.network ? t('auth.networkError') : result.error || t(fallbackKey);

  const invalid = (field) =>
    error?.field === field ? { 'aria-invalid': true, 'aria-describedby': 'auth-error' } : {};
  const errorFor = (field) => error?.field === field && (
    <p id="auth-error" role="alert" className={styles.fieldError}>
      <Icon name="error" />{error.message}
    </p>
  );

  const passwordToggle = (
    <button type="button" className={styles.passwordToggle} onClick={() => setShowPassword(!showPassword)}>
      {showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
    </button>
  );

  return (
    <div className={styles.authContainer}>
      <div className={styles.authSplit}>
      {/* The ministry's reversed lockup: the white mark on its own gradient */}
      <aside className={styles.brandPanel}>
        <img
          src={`${process.env.PUBLIC_URL}/moe-emblem-white.svg`}
          className={styles.brandMark}
          alt={language === 'ar' ? 'وزارة التعليم' : 'Ministry of Education'}
          draggable="false"
        />
      </aside>

      <main className={styles.formPanel}>
        {/* Appearance and language are available before signing in */}
        <div className={styles.authToolbar}>
          <button
            type="button"
            className={styles.langToggle}
            onClick={toggleTheme}
            title={language === 'ar' ? 'المظهر' : 'Appearance'}
            aria-label={language === 'ar' ? (theme === 'light' ? 'المظهر الداكن' : 'المظهر الفاتح') : (theme === 'light' ? 'Switch to dark appearance' : 'Switch to light appearance')}
          >
            <Icon name={theme === 'light' ? 'dark_mode' : 'light_mode'} />
          </button>
          <button type="button"
          className={styles.langToggle}
          onClick={toggleLanguage}
          title={language === 'en' ? 'العربية' : 'English'}
          aria-label={language === 'en' ? 'التبديل إلى العربية' : 'Switch to English'}
        >
          <Icon name="language" />
          <span>{language === 'en' ? 'ع' : 'EN'}</span>
        </button>
        </div>

        <div className={styles.authCard}>
        <div className={styles.authHeading}>
          <h1>{isLogin ? t('common.appName') : t('auth.signup')}</h1>
          <p>{isLogin ? t('auth.tagline') : t('auth.signupIntro')}</p>
        </div>

        {/* Login Form */}
        {isLogin ? (
          <form className={styles.authForm} onSubmit={handleLogin}>
            <div className={styles.formGroup}>
              <label htmlFor="login-username">{t('auth.username') || 'Username or Email'}</label>
              <input
id="login-username"
                {...invalid('username')}
                autoComplete="username"
                type="text"
                name="username"
                placeholder={t('auth.usernamePlaceholder') || 'Enter username or email'}
                value={loginData.username}
                onChange={handleLoginInputChange}
                disabled={loading}
                required
              />
              {errorFor('username')}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="login-password">{t('auth.password') || 'Password'}</label>
              <div className={styles.passwordField}>
                <input
                  id="login-password"
                  {...invalid('password')}
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('auth.passwordPlaceholder') || 'Enter password'}
                  value={loginData.password}
                  onChange={handleLoginInputChange}
                  disabled={loading}
                  required
                />
                {passwordToggle}
              </div>
              {errorFor('password')}
            </div>

            {errorFor('form')}
            <button
              type="submit"
              className={styles.authButton}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="progress_activity" spin />
                  {t('auth.loggingIn') || 'Logging in...'}
                </>
              ) : (
                t('auth.login') || 'Login'
              )}
            </button>
          </form>
        ) : (
          // Signup Form
          <form className={styles.authForm} onSubmit={handleSignup}>
            <div className={styles.formGroup}>
              <label htmlFor="signup-username">{t('auth.username') || 'Username'}</label>
              <input
id="signup-username"
                {...invalid('username')}
                autoComplete="username"
                type="text"
                name="username"
                placeholder={t('auth.usernamePlaceholder') || 'Choose a username'}
                value={signupData.username}
                onChange={handleSignupInputChange}
                disabled={loading}
                required
              />
              {errorFor('username')}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="signup-email">{t('auth.email') || 'Email'}</label>
              <input
id="signup-email"
                {...invalid('email')}
                autoComplete="email"
                type="email"
                name="email"
                placeholder={t('auth.emailPlaceholder') || 'Enter your email'}
                value={signupData.email}
                onChange={handleSignupInputChange}
                disabled={loading}
                required
              />
              {errorFor('email')}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="signup-password">{t('auth.password') || 'Password'}</label>
              <div className={styles.passwordField}>
                <input
                  id="signup-password"
                  {...invalid('password')}
                  autoComplete="new-password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('auth.passwordPlaceholder') || 'Create a password'}
                  value={signupData.password}
                  onChange={handleSignupInputChange}
                  disabled={loading}
                  required
                  minLength={6}
                />
                {passwordToggle}
              </div>
              {errorFor('password')}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="signup-confirm">{t('auth.confirmPassword') || 'Confirm Password'}</label>
              <input
id="signup-confirm"
                {...invalid('confirmPassword')}
                autoComplete="new-password"
                type="password"
                name="confirmPassword"
                placeholder={t('auth.confirmPasswordPlaceholder') || 'Confirm your password'}
                value={signupData.confirmPassword}
                onChange={handleSignupInputChange}
                disabled={loading}
                required
              />
              {errorFor('confirmPassword')}
            </div>

            {errorFor('form')}
            <button
              type="submit"
              className={styles.authButton}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="progress_activity" spin />
                  {t('auth.signingUp') || 'Creating account...'}
                </>
              ) : (
                t('auth.signup') || 'Sign Up'
              )}
            </button>
          </form>
        )}

        {/* Guest Mode */}
        <div className={styles.guestMode}>
          <p>{t('auth.or') || 'Or'}</p>
          <button
            className={styles.guestButton}
            onClick={handleGuestContinue}
            disabled={loading}
          >
            <Icon name="person" />
            {t('auth.continueAsGuest') || 'Continue as Guest'}
          </button>
        </div>

        <p className={styles.authSwitch}>
          {isLogin ? t('auth.dontHaveAccount') : t('auth.alreadyHaveAccount')}
          <button type="button" className={styles.switchLink} onClick={() => { setIsLogin(!isLogin); setShowPassword(false); setError(null); }} disabled={loading}>
            {isLogin ? t('auth.signup') : t('auth.login')}
          </button>
        </p>
        </div>
      </main>
      </div>
    </div>
  );
};

export default Auth;